import { useState, useEffect, type RefObject } from 'react';

/**
 * Hook para calcular la posición de un elemento "fantasma" que hace de fondo
 * persiguiendo al botón del menú que se encuentra activo en base a la ruta actual.
 */
export function useSidebarIndicator(
	navRef: RefObject<HTMLElement | null>,
	pathname: string,
	compact: boolean
) {
	const [indicator, setIndicator] = useState({ top: 0, height: 0, opacity: 0, ready: false, animate: false });

	useEffect(() => {
		const nav = navRef.current;
		if (!nav) return;

		// Item activo que el ResizeObserver está midiendo en este momento.
		let observedActive: HTMLElement | null = null;
		let sizeObserver: ResizeObserver | null = null;
		let enableAnimation: ReturnType<typeof setTimeout> | null = null;

		const updateIndicator = () => {
			if (!navRef.current) return;
			// Buscamos cuál es el tag <a> que tiene la propiedad aria-current="page" (el activo)
			const activeEl = navRef.current.querySelector('[aria-current="page"]') as HTMLElement | null;

			if (activeEl) {
				// El activo cambia al navegar o cuando RoleGuard resuelve: reapuntamos el
				// observer para seguir midiendo el elemento que realmente se está pintando.
				if (activeEl !== observedActive) {
					if (observedActive) sizeObserver?.unobserve(observedActive);
					sizeObserver?.observe(activeEl);
					observedActive = activeEl;
				}

				const navRect = navRef.current.getBoundingClientRect();
				const activeRect = activeEl.getBoundingClientRect();
				const top = activeRect.top - navRect.top;
				const height = activeRect.height;

				setIndicator(prev => {
					// El observer dispara en cada frame de la animación; si la medición no
					// cambió, cortamos acá para no encadenar renders idénticos.
					if (prev.ready && prev.top === top && prev.height === height && prev.opacity === 1) {
						return prev;
					}

					const isFirstLoad = !prev.ready;

					if (isFirstLoad) {
						// La primera colocación va DE GOLPE: la píldora aparece donde
						// corresponde en vez de viajar hasta ahí desde el borde de arriba.
						//
						// Y la animación no se habilita al frame siguiente, sino pasados los
						// 300ms que dura `transition: all .3s` de los items. Ése era el
						// problema del menú de celular: el panel monta este menú de cero cada
						// vez que se abre, los items animan su alto durante ese lapso, el
						// ResizeObserver mide cada frame —para eso está— y con la animación ya
						// encendida la píldora perseguía cada medición con su propia
						// transición de 0.4s. Se veía bajar desde arriba hasta acomodarse.
						// 350 y no 300: hay que llegar DESPUÉS de la última medición, no justo
						// encima.
						enableAnimation = setTimeout(() => {
							setIndicator(p => ({ ...p, animate: true }));
						}, 350);
					}

					return {
						...prev,
						top,
						height,
						opacity: 1,
						ready: true,
						animate: isFirstLoad ? false : prev.animate
					};
				});
			} else {
				if (observedActive) {
					sizeObserver?.unobserve(observedActive);
					observedActive = null;
				}
				setIndicator(prev => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
			}
		};

		// Los breakpoints por altura del sidebar cambian `--sb-rail`, y los items animan
		// ese alto durante 300ms (.item { transition: all .3s }). Un handler de 'resize'
		// mide una sola vez, en pleno vuelo, y deja la píldora con el alto intermedio.
		// El observer mide cada frame de esa transición, así que converge al valor final
		// sin importar en qué momento el usuario suelte la ventana o cambie de pantalla.
		sizeObserver = new ResizeObserver(() => updateIndicator());
		sizeObserver.observe(nav);

		// Ejecutamos para calcular la posición una vez que el DOM haya re-renderizado
		updateIndicator();

		// Lo volvemos a correr 100ms después para asegurarnos que cualquier animación del Sidebar
		// (expandir/contraer) no haya quebrado la posición, recalculándola en caso preciso.
		const timeout = setTimeout(updateIndicator, 100);

		// Usamos un MutationObserver para reaccionar a cambios en los hijos (ej. RoleGuard renderizando ítems diferidos)
		const observer = new MutationObserver(() => {
			updateIndicator();
		});

		observer.observe(nav, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['aria-current']
		});

		// Red de seguridad: mover la ventana entre pantallas puede reacomodar el layout
		// sin que el alto del nav llegue a cambiar (otro DPI, misma grilla de items).
		window.addEventListener('resize', updateIndicator);

		return () => {
			clearTimeout(timeout);
			if (enableAnimation) clearTimeout(enableAnimation);
			observer.disconnect();
			sizeObserver?.disconnect();
			window.removeEventListener('resize', updateIndicator);
		};
	}, [pathname, compact, navRef]);

	return indicator;
}
